import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  getContentEntityDetailsByIdMultilingual,
  getContentEntitySEOById,
  upsertContentEntitySEO,
  useContentSelector,
} from '@wajeez-web/stores/content';
import { Server } from '@wajeez/wajeez.microservices';
import { Languages } from '@wajeez-web/core/utils';
import { WajeezFormInput } from '@wajeez-web/components/form-builder';
import { Button, LanguagesLayout } from '@wajeez-web/components/ui';
import {
  AlertType,
  CommonActions,
  useCommonDispatch,
} from '@wajeez-web/stores/common';

interface ContentEntitySEOProps {
  id: string;
}

export const ContentEntitySEO = ({ id }: ContentEntitySEOProps) => {
  const { data, status } = useContentSelector(
    ({ contentEntity }) => contentEntity.contentEntitySEOById
  );

  useEffect(() => {
    getContentEntitySEOById(id);
  }, [id]);

  return status === 'loading' ? (
    <div></div>
  ) : data ? (
    <ContentEntitySEOForm data={data} id={id} />
  ) : (
    <ContentEntitySEOForm data={undefined} id={id} />
  );
};

const ContentEntitySEOForm = ({
  data,
  id,
}: {
  data: Server.GetContentEntitySeoMultilingualOutput | undefined;
  id: string;
}) => {
  const navigate = useNavigate();

  const commonDispatch = useCommonDispatch();

  const status = useContentSelector(
    ({ contentEntity }) => contentEntity.upsertContentEntitySEO.status
  );

  const { t } = useTranslation();

  const {
    control,
    handleSubmit,
    getValues,
    setError,
    formState: { errors },
  } = useForm<Server.UpsertContentEntitySeoCommand>({
    defaultValues: data
      ? {
          ...data,
          profiles: !data.profiles?.length
            ? [
                {
                  languageId: Languages.AR,
                },
              ]
            : data.profiles,
        }
      : { profiles: [{ languageId: Languages.AR }] },
  });

  const {
    fields: profiles,
    append: appendProfile,
    remove: removeProfile,
  } = useFieldArray<any, 'profiles', 'languageId'>({
    control,
    name: 'profiles',
  });

  const renderProfile = (language: Languages) => {
    const profiles = getValues('profiles');

    const currentProfile = profiles.find((p: any) => p.languageId === language);

    const selectedResponseProfileIndex = profiles.findIndex(
      (p) => p.languageId === language
    );

    const isRtl =
      (currentProfile?.languageId as number | undefined)?.toString() ==
      Languages.AR.toString();

    return (
      <div
        key={`profiles.${currentProfile?.languageId}.title`}
        className="w-full"
      >
        <div className={`md:col-span-${3} col-span-${3} `}>
          <WajeezFormInput
            control={control}
            name={`profiles.${selectedResponseProfileIndex}.title`}
            type={{
              fieldType: Server.FieldType.Text,
              fieldOptions: {},
              label: 'Title',
              placeHolder: 'Title',
            }}
            isReadOnly={false}
            setError={setError}
          />
        </div>
        <div className={`md:col-span-${3} col-span-${3} `}>
          <WajeezFormInput
            control={control}
            name={`profiles.${selectedResponseProfileIndex}.keywords`}
            type={{
              fieldType: Server.FieldType.Text,
              fieldOptions: { dynamic: true },
              label: 'Keywords',
              placeHolder: 'Keywords',
            }}
            isReadOnly={false}
            setError={setError}
          />
        </div>
        <div className={`md:col-span-${3} col-span-${3} `}>
          <WajeezFormInput
            control={control}
            name={`profiles.${selectedResponseProfileIndex}.description`}
            type={{
              fieldType: Server.FieldType.AreaText,
              fieldOptions: {
                rows: 4,
              },
              label: 'Description',
            }}
            isReadOnly={false}
            setError={setError}
          />
        </div>
        <div className={`md:col-span-${3} col-span-${3} `}>
          <WajeezFormInput
            control={control}
            name={`profiles.${selectedResponseProfileIndex}.pageDescription`}
            type={{
              fieldType: Server.FieldType.AreaText,
              fieldOptions: {
                rows: 4,
              },
              label: 'Page Description',
            }}
            isReadOnly={false}
            setError={setError}
          />
        </div>
        <div className="mt-8 h-[1px] w-full"></div>
      </div>
    );
  };

  return (
    <div className="w-full relative">
      <div
        className={`absolute top-0 rtl:left-4 ltr:right-4 flex justify-end space-s-2 rounded-md`}
      >
        <Button
          title="Cancel"
          disabled={status === 'loading'}
          onClick={() => {
            Swal.fire({
              title: t(`general.${'Are you sure?'}`, '') as string,
              text: t(
                `general.${"You won't be able to revert this!"}`,
                ''
              ) as string,
              icon: 'warning',
              showCancelButton: true,
              confirmButtonColor: '#3085d6',
              cancelButtonColor: '#d33',
              confirmButtonText: t(`general.${'Yes'}`, '') as string,
              cancelButtonText: t(`general.${'Cancel'}`, '') as string,
            }).then((result) => {
              if (result.isConfirmed) {
                navigate(-1);
              }
            });
          }}
          bg="transparent"
          type="ghost"
          color="textPrimary"
        />
        <Button
          loading={status === 'loading'}
          title="save"
          onClick={async () => {
            let isError = false;

            if (JSON.stringify(errors) !== '{}') {
              for (const key in errors) {
                const typedKey = key as keyof typeof errors;

                const stringifiedError = JSON.stringify(errors[typedKey]);

                if (
                  errors[typedKey] &&
                  stringifiedError !== '[]' &&
                  stringifiedError !== '{}'
                ) {
                  isError = true;

                  break;
                }
              }
            }

            if (!isError) {
              handleSubmit(
                (data) => {
                  upsertContentEntitySEO(data, () => {
                    getContentEntityDetailsByIdMultilingual(id);
                  });
                },
                (e) => console.log(e)
              )();
            } else {
              commonDispatch(
                CommonActions.setAlert({
                  for: 'page',
                  show: true,
                  type: AlertType.ERROR,
                  messages: [
                    {
                      code: 400,
                      field: null,
                      message: t(
                        `general.${'Enter the required fields!'}`,
                        'Enter the required fields!'
                      ),
                    },
                  ],
                })
              );
            }
          }}
          bg="primary"
          color="white"
        />
      </div>
      <div className={`pt-6 md:col-span-${3} col-span-${3} `}>
        <WajeezFormInput
          control={control}
          name={`slug`}
          type={{
            fieldType: Server.FieldType.Text,
            fieldOptions: {},
            label: 'Slug',
            placeHolder: 'Slug',
          }}
          isReadOnly={false}
          setError={setError}
        />
      </div>
      <LanguagesLayout
        renderPage={renderProfile}
        defaultLanguages={
          profiles.map((s) => s.languageId) as unknown as Languages[]
        }
        onAdd={function (language: Languages): void {
          const currentProfile = profiles?.[0];

          appendProfile({
            languageId: language,
            title: '',
            description: '',
          });
        }}
        onDelete={function (language: Languages): void {
          const selectedResponseProfileIndex = profiles.findIndex(
            (p) => (p.languageId as unknown as Languages) === language
          );
          removeProfile(selectedResponseProfileIndex);
        }}
      />
    </div>
  );
};
